import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import ProductSelectDialog, {
  ProductSelectModeEnum,
} from 'components/ProductSelectDialog';
import {
  enum2NumberArray,
  intentionallyFloatingPromiseReturn,
} from 'core/utils/commonUtils';
import {
  changeProductCount,
  getMeasureName,
  getProductTypeName,
} from 'core/utils/productUtils';
import { isNil, sumBy } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { MeasureEnum } from 'models/enums/measureEnum';
import { ProductTypeEnum } from 'models/enums/productTypeEnum';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement, SelectElement } from 'react-hook-form-mui';
import { object, string, number, array } from 'yup';
import ComponentDialog from './ComponentDialog';
import TagsAutocomplete from './TagsAutocomplete';
import type { ComponentForm } from './ComponentDialog';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridRowIdGetter,
} from '@mui/x-data-grid';
import type { Product } from 'models/product/product';
import type { ProductComponent, ProductDetail } from 'models/product/productDetail';
import type { FC } from 'react';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';

type ProductFormData = Pick<Product, 'name' | 'price' | 'measure' | 'type' | 'tags'>;

const ProductForm: FC<ProductFormProps> = ({
  product,
  onSubmitValid: onValid,
  onSubmitInvalid: onInvalid,
}) => {
  const [openProductSelectDialog, setOpenProductSelectDialog] = useState(false);
  const [openComponentEditDialog, setOpenComponentEditDialog] = useState(false);
  const [editedComponent, setEditedComponent] = useState<ProductComponent>();
  const [components, setComponents] = useState<ProductComponent[]>([]);
  const [isComposite, setIsComposite] = useState(
    product?.type === ProductTypeEnum.Composite
  );
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>();

  const componentDeletionConfirm = useConfirm();

  const deleteComponent = async (component: ProductComponent): Promise<void> => {
    await componentDeletionConfirm({
      description: `Удалить "${component.name ?? ''}"?`,
    })
      .then(() => {
        setComponents(components.filter((c) => c !== component));
        return true;
      })
      .catch(() => false);
  };

  const handleComponentDelete = async (component: ProductComponent): Promise<void> => {
    setEditedComponent(component);
    await deleteComponent(component);
  };

  const schema = object<ProductFormData>({
    name: string().max(500).required(),
    price: number().max(100_000_000).min(0).required(),
    measure: number().oneOf(enum2NumberArray(MeasureEnum)).required(),
    tags: array(),
  }).required();

  const formContext = useForm<ProductFormData>({
    mode: 'onTouched',
    defaultValues: product,
    resolver: yupResolver(schema),
  });

  const data = formContext.watch();

  const saveData: SubmitHandler<ProductFormData> = useCallback(
    (data) => {
      if (!isNil(onValid)) {
        const editedProduct = {
          ...product,
          ...data,
          id: product?.id ?? 0,
          tags: data.tags,
          components,
        } satisfies ProductDetail;

        onValid(editedProduct);
      }
    },
    [onValid, product, components]
  );

  const saveInvalidData: SubmitErrorHandler<ProductFormData> = useCallback(() => {
    !isNil(onInvalid) && onInvalid();
  }, [onInvalid]);

  useEffect(() => {
    if (formContext.formState.isValid && !formContext.formState.isValidating) {
      saveData(data);
    } else if (!formContext.formState.isValidating) {
      saveInvalidData(formContext.formState.errors);
    }
  }, [
    formContext.formState,
    data,
    formContext.formState.isValidating,
    saveData,
    saveInvalidData,
  ]);

  useEffect(() => {
    formContext.reset(product);
    setIsComposite(product?.type === ProductTypeEnum.Composite);
    setComponents(product?.components ?? []);

    const isAllComponentsPrintable =
      product?.components.every((c) => !c.notPrintInDocuments) ?? true;
    setColumnVisibilityModel({
      notPrintInDocuments: !isAllComponentsPrintable,
    });
  }, [product, formContext]);

  const handleTypeChange = (v: ProductTypeEnum): void => {
    const composite = v === ProductTypeEnum.Composite;
    if (composite) setPriceByComponents();
    setIsComposite(composite);
  };

  const setPriceByComponents: VoidFunction = () => {
    formContext.setValue(
      'price',
      sumBy(components, (c) => c.sum)
    );
  };

  const handleComponentsDialogCancel: VoidFunction = () => {
    setOpenComponentEditDialog(false);
  };

  const handleComponentDialogSubmit = (form: ComponentForm): void => {
    if (!isNil(editedComponent)) {
      changeProductCount(editedComponent, form.count);
      editedComponent.notPrintInDocuments = form.notPrintInDocuments;

      setEditedComponent(editedComponent);
      setPriceByComponents();

      const isAllComponentsPrintable =
        components.every((c) => !c.notPrintInDocuments) ?? true;
      setColumnVisibilityModel({
        notPrintInDocuments: !isAllComponentsPrintable,
      });
    }

    setOpenComponentEditDialog(false);
  };

  const handleEditComponent = (component: ProductComponent): void => {
    setEditedComponent(component);
    setOpenComponentEditDialog(true);
  };

  const columns: Array<GridColDef<ProductComponent>> = [
    {
      field: 'name',
      headerName: 'Название',
      sortable: false,
      display: 'flex',
      flex: 1,
    },
    {
      field: 'price',
      headerName: 'Цена',
      type: 'number',
      sortable: false,
      display: 'flex',
    },
    {
      field: 'count',
      headerName: 'Количество',
      sortable: false,
      display: 'flex',
      type: 'number',
      renderCell: (params) =>
        `${params.row.count} ${getMeasureName(params.row.measure, true)}`,
    },
    {
      field: 'sum',
      headerName: 'Стоимость',
      type: 'number',
      sortable: false,
      display: 'flex',
    },
    {
      field: 'notPrintInDocuments',
      headerName: 'Не отображать в документах',
      type: 'boolean',
      sortable: false,
      display: 'flex',
      renderCell: (params) =>
        params.row.notPrintInDocuments ? <CheckCircleOutlineOutlinedIcon /> : <></>,
    },
    {
      field: 'edit',
      // headerName: 'Стоимость',
      type: 'actions',
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title='Редактировать'>
            <IconButton
              aria-label='edit'
              onClick={() => {
                handleEditComponent(params.row);
              }}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Удалить'>
            <IconButton
              aria-label='delete'
              onClick={() => {
                intentionallyFloatingPromiseReturn(handleComponentDelete)(params.row);
              }}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const editComponents: VoidFunction = () => {
    setOpenProductSelectDialog(true);
  };

  const componentsSelectCancelHandler: VoidFunction = () => {
    setOpenProductSelectDialog(false);
  };

  const componentsSelectSaveHandler = (selectedComponents: Product[]): void => {
    let newComponents: ProductComponent[] = selectedComponents.map((c) => ({
      componentProductId: c.id,
      count: 0,
      measure: c.measure,
      name: c.name,
      price: c.price,
      sum: 0,
      notPrintInDocuments: false,
    }));

    newComponents = [...newComponents, ...components];
    setComponents(newComponents);
    setOpenProductSelectDialog(false);
  };

  const getRowId: GridRowIdGetter<ProductComponent> = (row) => row.componentProductId;

  return (
    <>
      <FormContainer
        formContext={formContext}
        onSuccess={saveData}
        onError={saveInvalidData}>
        <TextFieldElement
          fullWidth
          label='Название'
          name='name'
          type='text'
          margin='normal'
          required
        />

        <TextFieldElement
          fullWidth
          label='Цена'
          name='price'
          type='number'
          margin='normal'
          slotProps={{
            htmlInput: { readOnly: isComposite },
            input: { endAdornment: <CurrencyRubleIcon /> },
          }}
          helperText={
            isComposite && 'Рассчитывается автоматически на основании комплектующих'
          }
          required
        />

        <SelectElement
          fullWidth
          label='Единица измерения'
          name='measure'
          margin='normal'
          required
          options={enum2NumberArray(MeasureEnum).map((v) => ({
            id: v,
            label: getMeasureName(v),
          }))}
        />
        <SelectElement
          fullWidth
          label='Тип'
          name='type'
          margin='normal'
          required
          onChange={handleTypeChange}
          options={enum2NumberArray(ProductTypeEnum).map((v) => ({
            id: v,
            label: getProductTypeName(v),
          }))}
        />

        <TagsAutocomplete name='tags' />
      </FormContainer>

      {isComposite && (
        <>
          <ComponentDialog
            open={openComponentEditDialog}
            productComponent={editedComponent}
            onCancel={handleComponentsDialogCancel}
            onSubmit={handleComponentDialogSubmit}
          />
          <ProductSelectDialog
            open={openProductSelectDialog}
            typeFilter={ProductTypeEnum.Simple}
            mode={ProductSelectModeEnum.Multiple}
            ignoreProductIds={components.map((c) => c.componentProductId)}
            onCancel={componentsSelectCancelHandler}
            onSave={componentsSelectSaveHandler}
          />
          <Stack direction={'row'} spacing={2} my={1.5} alignItems='center'>
            <Typography fontWeight={'bold'} variant='body1'>
              Комплектующие
            </Typography>
            <Button variant='outlined' size='small' onClick={editComponents}>
              Добавить
            </Button>
          </Stack>

          <DataGrid
            sx={{
              [`& .${gridClasses.cell}:not([data-field="actions"])`]: {
                py: 2,
              },
              [`& .${gridClasses.cell}:focus`]: {
                outline: 'none',
              },
            }}
            rows={components}
            getRowId={getRowId}
            hideFooterPagination
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu={true}
            getRowHeight={() => 'auto'}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
          />
        </>
      )}
    </>
  );
};

export interface ProductFormProps {
  product?: ProductDetail;
  onSubmitValid?: (product: ProductDetail) => void;
  onSubmitInvalid?: VoidFunction;
}

export default ProductForm;
